import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSDataItem,
  WSDataItemProps,
  WSGrid,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IEngagementResponse,
  IRequirementDefinitionResponse
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useDeleteRequirementFromEngagement } from "../../../query/engagements/mutations/useDeleteRequirementFromEngagement";
import { getEngagementLabelByType } from "../../Payers/utils/engagement";
import { getRequirementDefinitionDataItemProps } from "../../RequirementDefinitions/utils";
import { useExtendedRequirementDefinition } from "../../../query/requirementDefinitions/query/useExtendedRequirementDefinition";
import { selectorTemplateParamsFromExtendedRequirementDefinition } from "../../Requirements/modules/InsuranceCoverage/selectors/selectorTemplateParamsFromExtendedRequirementDefinition";
import { WSQueries } from "../../../query/WSQuery";

type RemoveRequirementFromEngagementModalProps = {
  engagement: IEngagementResponse;
  requirementDefinition: IRequirementDefinitionResponse;
  onClose: () => void;
};

const DataCell: React.FC<WSDataItemProps> = props => {
  return (
    <WSGrid.Item key={`${props.label}`} span={{ xs: "12", s: "6", l: "6" }}>
      <WSDataItem {...props} />
    </WSGrid.Item>
  );
};

const RemoveRequirementFromEngagementModal: React.FC<
  RemoveRequirementFromEngagementModalProps
> = ({ requirementDefinition, engagement, onClose }) => {
  const queryRequirement = useExtendedRequirementDefinition(
    requirementDefinition.requirementDefinitionId
  );
  const { openSnackbar } = useWSSnackbar();

  const [removedRequirement, removedRequirementMeta] =
    useDeleteRequirementFromEngagement();

  return (
    <WSQueries queries={{ queryRequirement }}>
      {({ queryRequirementData }) => (
        <WSList gap="XL">
          <WSText.ParagraphSm weight="book" color="gray500">
            Are you sure you want to remove this requirement from the engagement
            type?
          </WSText.ParagraphSm>

          <WSPanel shimmer={queryRequirement.isLoading}>
            <WSGrid gutter="XL">
              <DataCell
                label="Requirement name"
                value={requirementDefinition.name}
              />
              <DataCell label="Engagement name" value={engagement.name} />
              <DataCell
                {...getRequirementDefinitionDataItemProps(
                  selectorTemplateParamsFromExtendedRequirementDefinition(
                    queryRequirementData
                  )
                )}
                label="Requirement type"
              />
              <DataCell
                label="Engagement type"
                value={getEngagementLabelByType(engagement.type, {
                  descriptive: true
                })}
              />
            </WSGrid>
          </WSPanel>

          <WSAlert
            theme="info"
            icon="info-circle"
            size="M"
            title="Remove requirement from engagement type"
          >
            Once a requirement is removed from an engagement type, existing and
            future contractors will no longer need to complete it to be eligible
            to receive payments for that engagement.
          </WSAlert>

          <WSErrorMessage
            contextKey="Engagement"
            error={removedRequirementMeta.error}
          />

          <WSActions
            alignment="fill"
            sortButtons={false}
            buttons={[
              {
                label: "Cancel",
                kind: "Tertiary",
                type: "button",
                onClick() {
                  onClose();
                }
              },
              {
                label: "Remove requirement",
                kind: "Primary",
                destructive: true,
                onAsyncClick: async () => {
                  await removedRequirement(
                    {
                      engagementId: engagement.engagementId,
                      requirementDefinitionId:
                        requirementDefinition.requirementDefinitionId
                    },
                    {
                      onSuccess() {
                        openSnackbar({
                          message: "Requirement removed from engagement type",
                          type: "success"
                        });
                        onClose();
                      },
                      onError() {
                        openSnackbar({
                          message:
                            "Failed to remove requirement from engagement type",
                          type: "error"
                        });
                      }
                    }
                  );
                },
                type: "button"
              }
            ]}
          />
        </WSList>
      )}
    </WSQueries>
  );
};

export function useRemoveRequirementFromEngagementModal() {
  return useWSModal(RemoveRequirementFromEngagementModal, {
    title: "Remove requirement from engagement type",
    size: "M"
  });
}
